// BalancesLayout.jsx - Fixed Banner Loading + Full Admin Mode + DB Save/Load
import React, { useState, useEffect } from 'react';
import axios from "axios";
import config from '../config/config';
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import Banner from "../components/Banner";
import CategorySection from "../components/Categories";

// Import your admin components (create them if needed)
import FlashBarAdmin from "../pages/FlashBar";
import NavigationAdmin from "../pages/NavigationBar";
import BannerAdmin from "../pages/BannerAdmin";
import CategoryAdmin from "../pages/CategoryAdmin";

function BalancesLayout() {
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  // Section order (draggable/duplicable)
  const [sectionOrder, setSectionOrder] = useState([]);

  // Per-instance configs (maps keyed by unique ID)
  const [flashConfigs, setFlashConfigs] = useState({});
  const [navConfigs, setNavConfigs] = useState({});
  const [bannerConfigs, setBannerConfigs] = useState({});
  const [categoryConfigs, setCategoryConfigs] = useState({});

  // Which section is being edited (modal)
  const [editingSection, setEditingSection] = useState(null); // { type: 'banner', id: 'banner-2' }

  const CACHE_KEY = 'cms_layout_cache';
  const CACHE_TTL = 60 * 1000; // 1 minute

  useEffect(() => {
    const loadLayout = async () => {
      // Cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          if (data && Date.now() - timestamp < CACHE_TTL) {
            setSectionOrder(data.sectionOrder || []);
            setFlashConfigs(data.flashConfigs || {});
            setNavConfigs(data.navConfigs || {});
            setBannerConfigs(data.bannerConfigs || {});
            setCategoryConfigs(data.categoryConfigs || {});
            setLoading(false);
            return;
          }
        } catch (e) {
          localStorage.removeItem(CACHE_KEY);
        }
      }

      // No valid cache → fetch from DB
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const res = await axios.get(config.API_URL + '/api/pullLayoutSettings', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const db = res.data.layout || {};

        // Restore order
        setSectionOrder(db.sectionOrder || [
          { type: 'flashbar', id: 'flashbar-1' },
          { type: 'navbar', id: 'navbar-1' },
          { type: 'banner', id: 'banner-1' },
          { type: 'categories', id: 'categories-1' },
        ]);

        // Restore configs
        setFlashConfigs(db.configs?.flashbars || {});
        setNavConfigs(db.configs?.navbars || {});
        setBannerConfigs(db.configs?.banners || {});
        setCategoryConfigs(db.configs?.categories || {});

        // Cache fresh data
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: db,
          timestamp: Date.now(),
        }));
      } catch (error) {
        console.error("Load failed:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLayout();
  }, [token]);

  // Save full layout to DB
  const saveLayout = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = {
        sectionOrder,
        configs: {
          flashbars: flashConfigs,
          navbars: navConfigs,
          banners: bannerConfigs,
          categories: categoryConfigs,
        }
      };

      const res = await axios.post(config.API_URL + "/api/saveFullLayout", payload, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
        },
        withCredentials: true,
      });

      if (res.data.status === 1) {
        alert("Layout saved successfully! 🎉");
        localStorage.removeItem(CACHE_KEY); // Force refresh next load
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // Move section
  const moveSection = (from, to) => {
    const newOrder = [...sectionOrder];
    const [moved] = newOrder.splice(from, 1);
    newOrder.splice(to, 0, moved);
    setSectionOrder(newOrder);
  };

  // Duplicate section (independent config)
  const duplicateSection = (index) => {
    const section = sectionOrder[index];
    const newId = `${section.type}-${Date.now()}`;

    let newConfig;
    switch (section.type) {
      case 'flashbar':
        newConfig = { ...flashConfigs[section.id] };
        setFlashConfigs(prev => ({ ...prev, [newId]: newConfig }));
        break;
      case 'navbar':
        newConfig = { ...navConfigs[section.id] };
        setNavConfigs(prev => ({ ...prev, [newId]: newConfig }));
        break;
      case 'banner':
        newConfig = { ...bannerConfigs[section.id] };
        setBannerConfigs(prev => ({ ...prev, [newId]: newConfig }));
        break;
      case 'categories':
        newConfig = { ...categoryConfigs[section.id] };
        setCategoryConfigs(prev => ({ ...prev, [newId]: newConfig }));
        break;
      default:
        return;
    }

    setSectionOrder([
      ...sectionOrder.slice(0, index + 1),
      { ...section, id: newId },
      ...sectionOrder.slice(index + 1),
    ]);
  };

  // Delete section
  const deleteSection = (index) => {
    const section = sectionOrder[index];
    switch (section.type) {
      case 'flashbar':
        setFlashConfigs(prev => {
          const copy = { ...prev };
          delete copy[section.id];
          return copy;
        });
        break;
      case 'navbar':
        setNavConfigs(prev => {
          const copy = { ...prev };
          delete copy[section.id];
          return copy;
        });
        break;
      case 'banner':
        setBannerConfigs(prev => {
          const copy = { ...prev };
          delete copy[section.id];
          return copy;
        });
        break;
      case 'categories':
        setCategoryConfigs(prev => {
          const copy = { ...prev };
          delete copy[section.id];
          return copy;
        });
        break;
      default:
        break;
    }

    setSectionOrder(sectionOrder.filter((_, i) => i !== index));
  };

  // Render section
  const renderSection = (section) => {
    let config;
    switch (section.type) {
      case 'flashbar':
        config = flashConfigs[section.id] || { enabled: true };
        return <FlashBar config={config} />;
      case 'navbar':
        config = navConfigs[section.id] || {};
        return <NavigationBar config={config} />;
      case 'banner':
        config = bannerConfigs[section.id] || { preset: 'full-width', slides: [] };
        return <Banner config={config} />;
      case 'categories':
        config = categoryConfigs[section.id] || { preset: 'carousel', categories: [] };
        return <CategorySection config={config} />;
      default:
        return null;
    }
  };

  const renderWithControls = (section, index) => (
    <div key={section.id} className="relative mb-8">
      {renderSection(section)}

      {isAdminMode && (
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-xl flex gap-3 z-50">
          <button
            onClick={() => setEditingSection({ type: section.type, id: section.id })}
            className="p-2 bg-green-50 hover:bg-green-100 text-green-700 rounded"
          >
            Edit
          </button>
          <button
            onClick={() => moveSection(index, index - 1)}
            disabled={index === 0}
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            ↑
          </button>
          <button
            onClick={() => moveSection(index, index + 1)}
            disabled={index === sectionOrder.length - 1}
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            ↓
          </button>
          <button
            onClick={() => duplicateSection(index)}
            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded"
          >
            Duplicate
          </button>
          <button
            onClick={() => deleteSection(index)}
            className="p-2 bg-red-50 hover:bg-red-100 text-red-700 rounded"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );

  // Editing Modal
  if (editingSection) {
    const { type, id } = editingSection;
    let AdminComponent, currentCfg, updateFn;

    switch (type) {
      case 'flashbar':
        AdminComponent = FlashBarAdmin;
        currentCfg = flashConfigs[id];
        updateFn = (newCfg) => setFlashConfigs(prev => ({ ...prev, [id]: newCfg }));
        break;
      case 'navbar':
        AdminComponent = NavigationAdmin;
        currentCfg = navConfigs[id];
        updateFn = (newCfg) => setNavConfigs(prev => ({ ...prev, [id]: newCfg }));
        break;
      case 'banner':
        AdminComponent = BannerAdmin;
        currentCfg = bannerConfigs[id];
        updateFn = (newCfg) => setBannerConfigs(prev => ({ ...prev, [id]: newCfg }));
        break;
      case 'categories':
        AdminComponent = CategoryAdmin;
        currentCfg = categoryConfigs[id];
        updateFn = (newCfg) => setCategoryConfigs(prev => ({ ...prev, [id]: newCfg }));
        break;
      default:
        return null;
    }

    return (
      <div className="fixed inset-0 bg-black/70 z-50 overflow-auto flex items-center justify-center">
        <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl">
          <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
            <h2 className="text-2xl font-bold capitalize">Edit {type} Section</h2>
            <button onClick={() => setEditingSection(null)} className="text-3xl">&times;</button>
          </div>
          <div className="p-6">
            <AdminComponent
              config={currentCfg}
              onSave={(updated) => {
                updateFn(updated);
                setEditingSection(null);
              }}
              onClose={() => setEditingSection(null)}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-xl text-gray-600">Loading your store...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Admin Toggle */}
      <button
        onClick={() => setIsAdminMode(!isAdminMode)}
        className={`fixed top-6 right-6 z-50 px-6 py-3 rounded-full font-bold shadow-xl transition-all ${
          isAdminMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
        }`}
      >
        {isAdminMode ? 'Exit Edit Mode' : 'Edit Layout'}
      </button>

      {/* Sections */}
      {sectionOrder.map((section, index) => renderWithControls(section, index))}

      {/* Save Bar in Admin Mode */}
      {isAdminMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-lg px-10 py-5 rounded-full shadow-2xl flex gap-8 z-50">
          <button
            onClick={saveLayout}
            disabled={saving}
            className="bg-green-600 text-white px-8 py-3 rounded-full font-bold hover:bg-green-700 disabled:opacity-60 transition"
          >
            {saving ? 'Saving...' : 'Save Layout'}
          </button>
          <span className="text-sm text-gray-600">Drag • Duplicate • Edit • Delete</span>
        </div>
      )}
    </div>
  );
}

export default BalancesLayout;